/**
 * MARVY IT Services Logo — Circular badge with Cloud + AI Neural mark
 *
 * Design:
 *   • "MARVY" text arcs around the top of the circle
 *   • White cloud shape in the centre → Cloud computing
 *   • Cyan neural nodes + connections inside the cloud → AI
 *   • Data-stream lines + output layer below → AI processing
 *
 * Exports:
 *   LogoMark  – self-contained circular badge (MARVY text baked in)
 *   Logo      – badge + "IT SERVICES" subtitle  (dark, light backgrounds)
 *   LogoLight – badge + "IT SERVICES" subtitle  (light, dark backgrounds)
 */

import { useId } from 'react';

export function LogoMark({ size = 72, className = '' }) {
  // Unique IDs so multiple instances on the same page don't collide
  const uid    = useId().replace(/:/g, '_');
  const arcId  = `arc_${uid}`;
  const clipId = `clip_${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="MARVY IT Services"
    >
      <defs>
        {/* Clip everything to the circle */}
        <clipPath id={clipId}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>

        {/*
          Arc path for "MARVY" text.
          Short clockwise arc (0,1) from left-point to right-point
          passes over the TOP of the circle → text reads left-to-right.
          Radius 38 keeps the text inside the outer decorative ring.
        */}
        <path id={arcId} d="M 15,50 A 35,35 0 0,1 85,50" />
      </defs>

      {/* ── Background circle ────────────────────────────── */}
      <circle cx="50" cy="50" r="50" fill="#0c1b4d" />

      {/* Decorative outer ring */}
      <circle cx="50" cy="50" r="46" stroke="#C0C0C0" strokeWidth="1" fill="none" opacity="0.85" />

      {/* ── "MARVY" arc text ─────────────────────────────── */}
      <text
        fontSize="11"
        fontWeight="800"
        letterSpacing="6"
        fontFamily="Arial, Helvetica, sans-serif"
        fill="#D4AF37"
      >
        <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
          MARVY
        </textPath>
      </text>

      {/* ── Everything below is clipped to the circle ─────── */}
      <g clipPath={`url(#${clipId})`}>

        {/* Cloud shape (three overlapping white domes + base) */}
        <circle cx="31" cy="50" r="13"  fill="white" opacity="0.93" />
        <circle cx="50" cy="42" r="17"  fill="white" opacity="0.93" />
        <circle cx="69" cy="50" r="13"  fill="white" opacity="0.93" />
        <rect   x="19"  y="50" width="62" height="12" fill="white" opacity="0.93" />

        {/* Neural network connections (drawn before nodes) */}
        <line x1="50" y1="39" x2="32" y2="50" stroke="#06b6d4" strokeWidth="1.6" opacity="0.9" />
        <line x1="50" y1="39" x2="68" y2="50" stroke="#06b6d4" strokeWidth="1.6" opacity="0.9" />
        <line x1="32" y1="50" x2="50" y2="59" stroke="#06b6d4" strokeWidth="1.6" opacity="0.9" />
        <line x1="68" y1="50" x2="50" y2="59" stroke="#06b6d4" strokeWidth="1.6" opacity="0.9" />
        <line x1="32" y1="50" x2="68" y2="50" stroke="#06b6d4" strokeWidth="1"   opacity="0.5" />

        {/* Neural nodes */}
        <circle cx="50" cy="39" r="4"   fill="#06b6d4" />   {/* top-centre  */}
        <circle cx="32" cy="50" r="3.5" fill="#06b6d4" />   {/* left        */}
        <circle cx="68" cy="50" r="3.5" fill="#06b6d4" />   {/* right       */}
        <circle cx="50" cy="59" r="3.5" fill="#06b6d4" />   {/* base-centre */}

        {/* Data-stream lines from cloud base */}
        <line x1="36" y1="62" x2="36" y2="71" stroke="#06b6d4" strokeWidth="2"   strokeLinecap="round" opacity="0.7" />
        <line x1="50" y1="62" x2="50" y2="74" stroke="#06b6d4" strokeWidth="2.2" strokeLinecap="round" opacity="0.95" />
        <line x1="64" y1="62" x2="64" y2="71" stroke="#06b6d4" strokeWidth="2"   strokeLinecap="round" opacity="0.7" />

        {/* Output-layer nodes */}
        <circle cx="36" cy="72" r="3"   fill="#06b6d4" opacity="0.8" />
        <circle cx="50" cy="75" r="3.8" fill="#06b6d4" />
        <circle cx="64" cy="72" r="3"   fill="#06b6d4" opacity="0.8" />

        {/* Output-layer lateral connections */}
        <line x1="36" y1="72" x2="50" y2="75" stroke="#06b6d4" strokeWidth="1.2" opacity="0.5" />
        <line x1="50" y1="75" x2="64" y2="72" stroke="#06b6d4" strokeWidth="1.2" opacity="0.5" />
      </g>
    </svg>
  );
}

/** Dark version — badge + subtitle, for light backgrounds */
export function Logo({ size = 72, showSubtitle = true, className = '' }) {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <LogoMark size={size} />
      {showSubtitle && (
        <div className="leading-none select-none">
          <div
            className="font-bold tracking-widest text-gray-400 uppercase"
            style={{ fontSize: Math.round(size * 0.2) }}
          >
            IT Services
          </div>
        </div>
      )}
    </div>
  );
}

/** Light version — badge + subtitle, for dark backgrounds */
export function LogoLight({ size = 72, showSubtitle = true, className = '' }) {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <LogoMark size={size} />
      {showSubtitle && (
        <div className="leading-none select-none">
          <div
            className="font-bold tracking-widest text-blue-300 uppercase"
            style={{ fontSize: Math.round(size * 0.2) }}
          >
            IT Services
          </div>
        </div>
      )}
    </div>
  );
}
